import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useTrainingCourse, { generateUrls } from '@hooks/useTrainingCourse'
import useTrainingCourses from '@hooks/useTrainingCourses'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import TrainingCourseListItem from '../TrainingCourseListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const trainingCoursesPayload = useTrainingCourses({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: { include_training_modules_count: true },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTrainingCourses,
    filteredTrainingCoursesCount,
    hasFilteredTrainingCourses,
    loading,
  } = trainingCoursesPayload

  const { callbacks: { createOrDuplicateTrainingCourse } } = useTrainingCourse()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { trainingCoursesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Courses | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => createOrDuplicateTrainingCourse()}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey="trainingCourses"
        tabBarItems={[
          {
            href: trainingCoursesIndexUrl,
            icon: faGlobe,
            key: 'trainingCourses',
            title: 'Courses',
          },
        ]}
        title="Courses"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTrainingCourses && <ListBlankState />}

            {!loading && hasFilteredTrainingCourses && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Courses">
                <Box flexDirection="column">
                  {filteredTrainingCourses.map(trainingCourse => (
                    <TrainingCourseListItem key={trainingCourse.id} trainingCourse={trainingCourse} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTrainingCoursesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
